// src/components/inputs/PhoneInput.jsx
import React, { useEffect, useMemo, useRef } from 'react';
import { TextField, MenuItem, Box, InputAdornment, Select, FormControl, Typography } from '@mui/material';
import { useTheme } from '@emotion/react';
import i18next from 'i18next';
import { parsePhoneNumberFromString, getExampleNumber, isValidPhoneNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import { countries } from '../../constants/countres';

const PhoneInput = ({
    name = 'phone',
    error,
    required,
    helperText,
    type,
    selectedCountryCode = countries[0].phone,
    setSelectedCountryCode,
    phoneNumber,
    setPhoneNumber,
    placeholder,
    fullPhoneNumber,
}) => {
    const theme = useTheme();
    let lng = i18next.language;

    const processedRef = useRef(false);

    const parsedPhone = useMemo(() => {
        if (!fullPhoneNumber) return null;

        try {
            const parsed = parsePhoneNumberFromString(fullPhoneNumber);
            if (parsed) {
                const countryMatch = countries.find(c => c.code === parsed.country);
                if (countryMatch) {
                    return {
                        countryPhone: countryMatch.phone,
                        nationalNumber: parsed.nationalNumber
                    };
                }
            }
        } catch (e) {
            console.error('Error parsing phone number:', e);
        }
        return null;
    }, [fullPhoneNumber]);

    useEffect(() => {
        if (parsedPhone && !processedRef.current) {
            setSelectedCountryCode(parsedPhone.countryPhone);
            setPhoneNumber(parsedPhone.nationalNumber);
            processedRef.current = true;
        }
    }, [parsedPhone, setSelectedCountryCode, setPhoneNumber]);

    useEffect(() => {
        if (fullPhoneNumber) {
            processedRef.current = false;
        }
    }, [fullPhoneNumber]);

    const getPhonePlaceholder = () => {
        const country = countries.find(c => c.phone === selectedCountryCode);
        try {
            const exampleNumber = getExampleNumber(country.code, examples);
            return exampleNumber?.formatNational() || '';
        } catch (e) {
            return '';
        }
    };

    const handleCountryChange = (newCountryCode) => {
        setSelectedCountryCode(newCountryCode);
    };

    const handlePhoneChange = (value) => {
        const cleanValue = value.replace(/\D/g, '');
        setPhoneNumber(cleanValue);
    };

    const getCountryCode = (phoneCode) => {
        return countries.find(c => c.phone === phoneCode)?.code;
    };

    const validatePhone = () => {
        if (!phoneNumber) return { isValid: true, message: '' };

        const countryCode = getCountryCode(selectedCountryCode);
        const fullNumber = `+${selectedCountryCode}${phoneNumber}`;

        try {
            const isValid = isValidPhoneNumber(fullNumber, countryCode);
            if (!isValid) {
                return {
                    isValid: false,
                    message: lng === 'ar'
                        ? 'رقم الهاتف غير صحيح للدولة المختارة'
                        : 'Invalid phone number for selected country'
                };
            }
            return { isValid: true, message: '' };
        } catch (e) {
            return {
                isValid: false,
                message: lng === 'ar'
                    ? 'رقم الهاتف غير صحيح'
                    : 'Invalid phone number'
            };
        }
    };

    const validation = validatePhone();

    return (
        <FormControl fullWidth variant="outlined" sx={{ backgroundColor: theme.palette.background.input }}>
            <TextField
                type="tel"
                placeholder={getPhonePlaceholder()}
                value={phoneNumber}
                onChange={(e) => handlePhoneChange(e.target.value)}
                required={required}
                error={error || !validation.isValid}
                helperText={helperText || (!validation.isValid ? validation.message : '')}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Select
                                className='test'
                                value={selectedCountryCode}
                                onChange={(e) => handleCountryChange(e.target.value)}
                                sx={{
                                    // إزالة السهم من الـ Select
                                    '& .MuiSelect-icon': { display: 'none', paddingRight: 0, },
                                    display: 'flex',
                                    paddingRight: 0,
                                    alignItems: 'center',
                                    mr: -1,
                                    minWidth: '80px',
                                    height: '40px',
                                    '& .MuiSelect-select': { paddingRight: '0 !important' },
                                    // ✅ التغيير الوحيد: إضافة !important
                                    direction: 'ltr !important',
                                }} 
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            // ✅ التغيير الوحيد: إضافة !important
                                            direction: 'ltr !important',
                                        }
                                    }
                                }}
                            >
                                {countries.map((country) => (
                                    // ✅ التغيير الوحيد: إضافة !important
                                    <MenuItem key={country.code} value={country.phone} sx={{ direction: 'ltr !important' }}>
                                        {/* ✅ التغيير الوحيد: إضافة !important */}
                                        <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'center', direction: 'ltr !important', }}>
                                            <img
                                                loading="lazy"
                                                width="25"
                                                height="25"
                                                srcSet={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`}
                                                src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                                                alt=""
                                                style={{
                                                    marginRight: '8px',
                                                    borderRadius: '50%',
                                                    ...(lng === 'ar' && { marginRight: 0, marginLeft: '8px' })
                                                }} 
                                            />
                                            <Typography sx={{ color: '#a7a7a7' }}><bdi>{country.code}</bdi></Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                            <Box
                                sx={{
                                    width: '1px',
                                    height: '24px',
                                    backgroundColor: theme.palette.divider,
                                    mx: 1
                                }} 
                            />
                        </InputAdornment>
                    ),
                }}
                sx={{
                    // ✅ التغيير الوحيد: إضافة !important للـ TextField
                    direction: 'ltr !important',
                    '& .MuiOutlinedInput-input': {
                        paddingLeft: 0,
                        textAlign: lng === 'ar' ? 'left' : 'left',
                        height: '10px',
                        '&::placeholder': {
                            textAlign: 'left',
                        },
                    },
                    '& .MuiOutlinedInput-root': {
                        // ✅ التغيير الوحيد: تثبيت flexDirection
                        flexDirection: 'row !important',
                    },
                    '& .MuiInputAdornment-root': {
                        // ✅ التغيير الوحيد: تثبيت flexDirection
                        flexDirection: 'row !important',
                    }
                }}
                inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                }}
                label=""
                name={name || type}
            />
        </FormControl>
    );
};

export default PhoneInput;
